import {
  faArrowLeft,
  faComments,
  faCompress,
  faExpand,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TabButton } from "../components/Tabs";
import { boundsForList, fitBounds } from "../map/util";
import { useAccount, useCaMap, useWorkspaces } from "../state";
import { isExploration } from "./kind";
import { KindBadge } from "./KindBadge";
import { ParcelRow } from "./ParcelRow";
import { WorkspaceFiles } from "./WorkspaceFiles";
type Tab = "parcels" | "files";

// w-96 plus de marge aan de rechterkant (right-2).
const BAR_WIDTH_PX = 384 + 8;

export const WorkspaceBar = () => {
  const { workspaces, selectedWorkspaceId, selectWorkspace } = useWorkspaces();
  const chatEnabled = useAccount((s) => s.user?.features?.workspace);
  const map = useCaMap();
  const navigate = useNavigate();
  const deselectWorkspace = useCallback(() => selectWorkspace(null), [selectWorkspace]);
  const [tab, setTab] = useState<Tab>("parcels");
  const [expanded, setExpanded] = useState(false);

  const workspace = workspaces?.find((p) => p.id === selectedWorkspaceId);
  if (!workspace) return null;

  const parcels = workspace.parcels || [];
  const area = parcels.reduce((sum, p) => (p.area || 0) + sum, 0);
  const exploration = isExploration(workspace);

  // Een klik op de naam is een vraag om in te zoomen: dus altijd (niet "als het
  // relevant is"), en met de percelen naast deze balk in plaats van eronder.
  const zoomToParcels = () => {
    const bounds = boundsForList(parcels);
    if (map && !bounds.isEmpty()) fitBounds(map, bounds, 10, BAR_WIDTH_PX);
  };

  const sizeClass = expanded
    ? "left-2 sm:left-[33rem] right-2 top-16 bottom-2 overflow-hidden"
    : "right-2 top-16 w-96 h-[60vh] min-w-[280px] max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] resize overflow-hidden";

  return (
    <div
      className={`fixed z-10 ${sizeClass} bg-white rounded-lg shadow-lg border border-gray-200`}
      style={!expanded ? { direction: "rtl" } : undefined}
    >
      <div className="flex flex-col w-full h-full text-sm" style={{ direction: "ltr" }}>
        <div className="flex items-center justify-between p-3 pb-0">
          <div className="flex items-center min-w-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                selectWorkspace(workspace.id);
                navigate(`/workspaces/${workspace.id}`);
              }}
              className="hover:green-blue-700 pr-2 shrink-0"
              title={exploration ? "Open verkenningsrapport" : "Open projectrapport"}
              aria-label={exploration ? "Open verkenningsrapport" : "Open projectrapport"}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <button
              onClick={zoomToParcels}
              className="font-bold text-black hover:text-blue-500 truncate text-left min-w-0"
            >
              {workspace.name} ({area.toFixed()} m²)
            </button>
            <KindBadge workspace={workspace} className="ml-2" />
          </div>
          <div className="flex items-center shrink-0">
            {chatEnabled && (
              <button
                onClick={() => navigate(`/workspace/${workspace.id}`)}
                className="text-gray-400 hover:text-black p-1"
                title="Open workspace"
                aria-label="Open workspace"
              >
                <FontAwesomeIcon icon={faComments} />
              </button>
            )}
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-gray-400 hover:text-black p-1"
              title={expanded ? "Verkleinen" : "Vergroten"}
              aria-label={expanded ? "Verkleinen" : "Vergroten"}
            >
              <FontAwesomeIcon icon={expanded ? faCompress : faExpand} />
            </button>
            <button
              onClick={deselectWorkspace}
              className="text-gray-400 hover:text-black p-1"
              aria-label="Sluiten"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
        <div>
          <div className="flex border-bgap-1">
            <TabButton active={tab === "parcels"} onClick={() => setTab("parcels")}>
              {exploration ? "Shortlist" : "Percelen"}{" "}
              <span className="text-gray-400 font-normal">({parcels.length})</span>
            </TabButton>
            <TabButton active={tab === "files"} onClick={() => setTab("files")}>
              Bestanden
            </TabButton>
          </div>
        </div>
        <div className="overflow-y-auto min-h-0 flex-1">
          {tab === "parcels" ? (
            parcels.length === 0 ? (
              <p className="text-gray-400 text-xs p-2">
                Klik op een perceel op de kaart en voeg het toe
              </p>
            ) : (
              <ul className="space-y-1 p-2">
                {parcels.map((parcel) => (
                  <ParcelRow
                    key={parcel.id}
                    parcel={parcel}
                    workspaceId={workspace.id}
                    exploration={exploration}
                  />
                ))}
              </ul>
            )
          ) : (
            <WorkspaceFiles workspaceId={workspace.id} />
          )}
        </div>
      </div>
    </div>
  );
};
